import { Link } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import { newsList } from "@/mocks/news";
import type { MediaItem, MediaType } from "@/types/media";
import { useSeo } from "@/hooks/useSeo";
import MediaCard from "@/components/feature/MediaCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import SiteIntro from "@/pages/home/components/SiteIntro";

export default function Home() {
  useSeo({
    title: "星空传媒—优质影视内容聚合平台",
    description:
      "星空传媒为您提供最新的电视剧、电影和综艺资讯，涵盖多种影视类别，方便用户在线观看和了解影视动态。",
    keywords: "星空传媒,影视内容,电视剧,电影,综艺,影视资讯,影视平台",
  });

  const allMedia = mediaList as MediaItem[];

  const featured = [...allMedia]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 5);

  const latest = [...allMedia]
    .sort((a, b) => b.year - a.year || b.views - a.views)
    .slice(0, 8);

  const popular = [...allMedia].sort((a, b) => b.views - a.views).slice(0, 8);

  const counts: Record<MediaType, number> = {
    tv: allMedia.filter((item) => item.type === "tv").length,
    movie: allMedia.filter((item) => item.type === "movie").length,
    variety: allMedia.filter((item) => item.type === "variety").length,
  };

  const latestNews = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1));
  const featuredNews = latestNews[0];

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <section className="pt-10 md:pt-14">
          <SectionHeading
            title="分类频道"
            subtitle="按内容类型快速进入对应的影视板块"
            icon="ri-layout-grid-line"
          />
          <CategoryEntry counts={counts} />
        </section>

        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="最新影视内容"
            subtitle="按上线时间更新的影视作品"
            icon="ri-time-line"
            actionLabel="查看全部"
            actionTo="/category/tv"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {latest.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="热门影视推荐"
            subtitle="按播放热度排序的影视作品"
            icon="ri-fire-line"
            actionLabel="查看全部"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {popular.map((item, index) => (
              <MediaCard key={item.id} item={item} rank={index + 1} showViews />
            ))}
          </div>
        </section>

        <section className="pt-12 md:pt-16">
          <SectionHeading
            title="最新影视资讯"
            subtitle="掌握行业动态与作品消息"
            icon="ri-newspaper-line"
            actionLabel="更多资讯"
            actionTo="/news"
          />
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <NewsCard item={featuredNews} layout="vertical" />
            <div className="flex flex-col gap-3">
              {latestNews.slice(1, 4).map((item) => (
                <NewsCard key={item.id} item={item} layout="horizontal" />
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <SiteIntro />
        </section>
      </div>

      <div className="w-full border-t border-secondary-200 bg-background-100">
        <div className="mx-auto flex max-w-[1280px] flex-col items-start gap-3 px-4 py-6 text-sm text-foreground-500 sm:flex-row sm:items-center sm:justify-between md:px-6">
          <p>找不到想看的内容？试试在全站检索影视作品与资讯。</p>
          <Link
            to="/search?q=%E6%98%9F"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-search-line"></i>
            前往搜索
          </Link>
        </div>
      </div>
    </div>
  );
}